/**
 * コンプライアンス AI 判定結果の取得 API クライアント。
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';

export type ComplianceVerdict = 'pass' | 'warn' | 'block' | 'error';
export type ComplianceSeverity = 'low' | 'mid' | 'high';

export interface ComplianceViolation {
  rule_code: string;
  violated: boolean;
  severity: ComplianceSeverity;
  evidence: string;
}

export interface ComplianceAIReport {
  id: string;
  material_id: string;
  material_table: 'temporary_materials' | 'materials';
  organization_id: string;
  user_uid: string;
  industry_categories: string[] | null;
  ruleset_codes: string[] | null;
  ruleset_version_snapshot: Array<{ code: string; version: number }> | null;
  overall_verdict: ComplianceVerdict;
  violations: ComplianceViolation[];
  model: string;
  evaluation_seconds: number | null;
  error_message: string | null;
  created_at: string;
}

interface ListResponse {
  reports: ComplianceAIReport[];
}

export async function fetchComplianceAIReports(
  materialId: string,
  materialTable: 'temporary_materials' | 'materials' = 'temporary_materials',
): Promise<ComplianceAIReport[]> {
  const response = await apiClient.get<ListResponse>('/compliance/ai-reports', {
    params: { material_id: materialId, material_table: materialTable },
  });
  return response.data?.reports ?? [];
}

export function useComplianceAIReports(
  materialId: string | null | undefined,
  materialTable: 'temporary_materials' | 'materials' = 'temporary_materials',
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['compliance-ai-reports', materialTable, materialId],
    queryFn: () => fetchComplianceAIReports(materialId as string, materialTable),
    enabled: Boolean(materialId) && (options?.enabled ?? true),
    staleTime: 60 * 1000,
  });
}

export interface ComplianceRulesetSummary {
  code: string;
  name: string;
  applicable_industries: string[] | null;
  severity: ComplianceSeverity;
  version: number;
}

export interface ComplianceSettingsResponse {
  effective_compliance_enabled: boolean;
  rulesets: ComplianceRulesetSummary[];
}

export function useComplianceSettings() {
  return useQuery({
    queryKey: ['compliance-settings'],
    queryFn: async () => {
      const response = await apiClient.get<ComplianceSettingsResponse>('/compliance/settings');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export interface CompliancePreviewAsset {
  id: string;
  type?: string;
  asin?: string | string[] | null;
}

export interface CompliancePreviewResponse {
  effective_compliance_enabled: boolean;
  has_product_link: boolean;
  candidate_rulesets: ComplianceRulesetSummary[];
  product_compliance_defaults: Record<string, boolean>;
  auto_active_codes: string[];
}

export function useCompliancePreview(assets: CompliancePreviewAsset[]) {
  // assets の id 順を安定化したキーで cache を分ける。
  const key = assets
    .map((a) => `${a.id}:${a.type ?? ''}:${Array.isArray(a.asin) ? a.asin.join(',') : (a.asin ?? '')}`)
    .sort()
    .join('|');
  return useQuery({
    queryKey: ['compliance-preview', key],
    queryFn: async () => {
      const response = await apiClient.post<CompliancePreviewResponse>('/compliance/preview', {
        assets: assets.map((a) => ({ id: a.id, type: a.type, asin: a.asin })),
      });
      return response.data;
    },
    // assets が変わるたびに再取得。debounce はしないが結果はキャッシュ済み。
    staleTime: 30 * 1000,
  });
}

export interface KickEvaluationResponse {
  report: ComplianceAIReport | null;
}

export function useKickComplianceEvaluation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      materialId: string;
      materialTable: 'temporary_materials' | 'materials';
    }) => {
      const response = await apiClient.post<KickEvaluationResponse>('/compliance/ai-reports', {
        material_id: params.materialId,
        material_table: params.materialTable,
      });
      return response.data?.report ?? null;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ['compliance-ai-reports', variables.materialTable, variables.materialId],
      });
    },
  });
}
